import React, { useState, useMemo } from "react";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../DemoClient/Components/Card";
import { Badge, Button } from "retro";
import { Label } from "../../DemoClient/Components/label";
import { Input } from "../../DemoClient/Components/Input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../DemoClient/Components/Table";
import { Search, Plus, Settings } from "lucide-react";
import { mccData as initialMccData, binData as initialBinData, mccDataExtra } from "../../DemoClient/Data/mockData";

export default function MccConfiguration() {
    const [search, setSearch] = useState("");
    const [mccData, setMccData] = useState(initialMccData);
    const [binData, setBinData] = useState(initialBinData);
    const [showModal, setShowModal] = useState(false);
    const [editBin, setEditBin] = useState(null);

    const [modalData, setModalData] = useState({
        bin: "",
        cardType: "",
        mccs: [], // { code, include: true/false }
    });

    // BIN -> MCC mapping
    const binToMccMap = useMemo(() => {
        const map = {};
        mccData.forEach((mcc) => {
            mcc.bins.forEach((bin) => {
                if (!map[bin]) map[bin] = [];
                map[bin].push({
                    code: mcc.code,
                    name: mcc.name,
                });
            });
        });
        return map;
    }, [mccData]);

    // Filter bins based on search
    const filteredBins = binData.filter(
        (bin) =>
            bin.bin.includes(search) ||
            bin.binName.toLowerCase().includes(search.toLowerCase()) ||
            bin.brand.toLowerCase().includes(search.toLowerCase())
    );

    // Add new BIN
    const handleAddBin = () => {
        setEditBin(null);
        setModalData({
            bin: "",
            cardType: "",
            mccs: mccDataExtra.map((mcc) => ({ code: mcc.code, include: false })), // all unchecked
        });
        setShowModal(true);
    };

    // Edit existing BIN
    const handleEditBin = (bin) => {
        const associatedMccCodes = binToMccMap[bin.bin]?.map((mcc) => mcc.code) || [];
        setEditBin(bin);
        setModalData({
            bin: bin.bin,
            cardType: bin.cardType,
            mccs: mccDataExtra.map((mcc) => ({
                code: mcc.code,
                include: associatedMccCodes.includes(mcc.code),
            })),
        });
        setShowModal(true);
    };

    const handleSubmit = () => {
        const selectedBin = modalData.bin;
        const selectedMccs = modalData.mccs;

        // Update MCCs: include or exclude the selected bin
        const updatedMccData = mccData.map((mcc) => {
            const mccSelection = selectedMccs.find((c) => c.code === mcc.code);
            if (mccSelection?.include) {
                return { ...mcc, bins: Array.from(new Set([...mcc.bins, selectedBin])) };
            } else {
                return { ...mcc, bins: mcc.bins.filter((b) => b !== selectedBin) };
            }
        });

        setMccData(updatedMccData);
        setShowModal(false);
    };

    return (
        <div style={{ marginTop: "1.5rem" }}>
            <Card style={{ borderRadius: "0.5rem", border: "1px solid #E5E7EB", backgroundColor: "#fff" }}>
                <CardHeader>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", gap: "1rem" }}>
                        <div>
                            <CardTitle>BIN Rules</CardTitle>
                            <CardDescription>Manage BINs and associate multiple MCCs</CardDescription>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "12px", height: "36px" }}>
                            <div style={{ position: "relative", height: "36px", display: "flex", alignItems: "center", marginTop: "12px" }}>
                                <Search style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 16, height: 16, color: "#6B7280", pointerEvents: "none" }} />
                                <Input
                                    placeholder="Search BIN..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    style={{ width: 260, height: 36, paddingLeft: 36, paddingRight: 12, lineHeight: "36px" }}
                                />
                            </div>
                            <Button size="sm" onClick={handleAddBin} style={{ height: 36, padding: "0 14px", display: "flex", alignItems: "center", gap: 6 }}>
                                <Plus style={{ width: 16, height: 16 }} /> Add BIN
                            </Button>
                        </div>
                    </div>
                </CardHeader>

                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow style={{ backgroundColor: "#F3F4F6" }}>
                                <TableHead style={thStyle}>BIN</TableHead>
                                <TableHead style={thStyle}>BIN Name</TableHead>
                                <TableHead style={thStyle}>Network</TableHead>
                                <TableHead style={thStyle}>Card Type</TableHead>
                                <TableHead style={thStyle}>Associated MCCs</TableHead>
                                <TableHead style={{ ...thStyle, textAlign: "center" }}>Actions</TableHead>
                            </TableRow>
                        </TableHeader>

                        <TableBody>
                            {filteredBins.map((bin) => (
                                <TableRow key={bin.id} style={{ borderBottom: "1px solid #E5E7EB" }}>
                                    <TableCell>{bin.bin}</TableCell>
                                    <TableCell>{bin.binName}</TableCell>
                                    <TableCell>{bin.brand}</TableCell>
                                    <TableCell>{bin.cardType}</TableCell>
                                    <TableCell>
                                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                            {binToMccMap[bin.bin]?.length > 0
                                                ? binToMccMap[bin.bin].map((mcc) => (
                                                    <Badge key={mcc.code} variant="secondary" style={{ fontSize: 12 }}>{mcc.code}</Badge>
                                                ))
                                                : <span style={{ color: "#6B7280", fontSize: 14 }}>None</span>}
                                        </div>
                                    </TableCell>
                                    <TableCell style={{ textAlign: "center", cursor: "pointer" }} onClick={() => handleEditBin(bin)}>
                                        <Settings style={{ width: 16, height: 16 }} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Modal */}
            {showModal && (
                <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999 }}>
                    <div style={{ backgroundColor: "#fff", padding: 24, borderRadius: 8, width: 600, maxHeight: "90vh", overflowY: "auto" }}>
                        <h3 style={{ margin: 0, marginBottom: 8 }}>{editBin ? "Edit BIN" : "Add BIN"}</h3>
                        <p style={{ marginTop: 0, marginBottom: 16 }}>Select a BIN and associate multiple MCCs</p>

                        <div style={{ display: "grid", gap: 16 }}>
                            <div>
                                <Label>BIN</Label>
                                <select
                                    value={modalData.bin}
                                    onChange={(e) => {
                                        const selectedBin = e.target.value;
                                        const selectedBinData = binData.find(b => b.bin === selectedBin);
                                        const associatedMccs = binToMccMap[selectedBin]?.map(m => m.code) || [];
                                        setModalData({
                                            bin: selectedBin,
                                            cardType: selectedBinData?.cardType || "",
                                            mccs: mccDataExtra.map((mcc) => ({
                                                code: mcc.code,
                                                include: associatedMccs.includes(mcc.code),
                                            })),
                                        });
                                    }}
                                    style={{ width: "100%", padding: 8, border: "1px solid #d1d5db", borderRadius: 6 }}
                                >
                                    <option value="" disabled>Select BIN</option>
                                    {binData.map((bin) => (
                                        <option key={bin.id} value={bin.bin}>{bin.bin} - {bin.binName} ({bin.cardType})</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <Label>Associated MCCs (Include/Exclude)</Label>
                                <div style={{
                                    border: "1px solid #D1D5DB",
                                    borderRadius: 4,
                                    padding: "8px",
                                    maxHeight: 200,
                                    overflowY: "auto",
                                }}>
                                    {modalData.mccs.map((mcc, idx) => (
                                        <div key={mcc.code} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 0" }}>
                                            <input
                                                type="checkbox"
                                                checked={mcc.include}
                                                onChange={(e) => {
                                                    const updatedMccs = [...modalData.mccs];
                                                    updatedMccs[idx].include = e.target.checked;
                                                    setModalData({ ...modalData, mccs: updatedMccs });
                                                }}
                                            />
                                            <span>{mcc.code} - {mccDataExtra.find(m => mcc.code === m.code)?.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                                <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                                <Button onClick={handleSubmit}>{editBin ? "Save Changes" : "Add BIN"}</Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* Styles */
const thStyle = { fontWeight: 600, padding: "12px", textAlign: "left" };
const monoCell = { fontFamily: "monospace", fontWeight: 500 };
